import {Actions, Context} from "vuex-smart-module";
import {AxiosError, AxiosResponse} from "axios";
import {Store} from "vuex";

import WarehouseAdminState, {
    WarehouseAdminStatus
} from "@/store/modules/warehouse-admin/warehouse-admin-state";
import WarehouseAdminGetters from "@/store/modules/warehouse-admin/warehouse-admin-getters";
import WarehouseAdminMutations from "@/store/modules/warehouse-admin/warehouse-admin-mutations";

import root from "@/store/modules";
import {store} from "@/store";

import esquireClient from "@/modules/esquire-client";

import SaveWarehouseAdminProductRequest from "@/http/requests/save-warehouse-admin-product-request";
import SaveWarehouseAdminProductSupplierRequest from "@/http/requests/save-warehouse-admin-product-supplier-request";
import SaveWarehouseAdminProductBuildingSpecificationRequest from "@/http/requests/save-warehouse-admin-product-building-specification-request";

import SaveWarehouseAdminProductLocationRequest from "@/http/requests/save-warehouse-admin-product-location-request";

import WarehouseAdminProductListData, {
    IWarehouseAdminProductListData
} from "@/http/data/warehouse-admin-product-list-data";
import WarehouseAdminProductData, {
    IWarehouseAdminProductData
} from "@/http/data/warehouse-admin-product-data";
import WarehouseAdminOptionsData, {
    IWarehouseAdminOptionsData
} from "@/http/data/warehouse-admin-options-data";
import WarehouseAdminProductsRequest from "@/http/requests/warehouse-admin-products-request";

import WarehouseAdminOrderData, {
    IWarehouseAdminOrderData
} from "@/http/data/warehouse-admin-order-data";

import WarehouseAdminOrdersRequest from "@/http/requests/warehouse-admin-orders-request";

import UpdateWarehouseAdminOrderItemLoadedRequest from "@/http/requests/update-warehouse-admin-order-item-loaded-request";

import UpdateWarehouseAdminOrderItemDeliveredRequest from "@/http/requests/update-warehouse-admin-order-item-delivered-request";

import UpdateWarehouseAdminOrderItemReturnedRequest from "@/http/requests/update-warehouse-admin-order-item-returned-request";

import ConfirmWarehouseAdminOrderDeliveryRequest, {
    WarehouseDeliveryConfirmationRole
} from "@/http/requests/confirm-warehouse-admin-order-delivery-request";

import WarehouseAdminStockProductData, {
    IWarehouseAdminStockProductData
} from "@/http/data/warehouse-admin-stock-product-data";

import SaveWarehouseAdminStockRequest from "@/http/requests/save-warehouse-admin-stock-request";

import WarehouseAdminRestockData, {
    IWarehouseAdminRestockData
} from "@/http/data/warehouse-admin-restock-data";

import SaveWarehouseAdminRestockRequest from "@/http/requests/save-warehouse-admin-restock-request";

import OrderWarehouseAdminRestockRequest from "@/http/requests/order-warehouse-admin-restock-request";

import CheckWarehouseAdminRestockReceiptRequest from "@/http/requests/check-warehouse-admin-restock-receipt-request";

import WarehouseAdminRetourData, {
    IWarehouseAdminRetourData
} from "@/http/data/warehouse-admin-retour-data";
import WarehouseAdminRetoursRequest from "@/http/requests/warehouse-admin-retours-request";
import SaveWarehouseAdminRetourRequest from "@/http/requests/save-warehouse-admin-retour-request";
import UpdateWarehouseAdminRetourRequest from "@/http/requests/update-warehouse-admin-retour-request";
import WarehouseAdminRestockHistoryRequest  from "@/http/requests/warehouse-admin-restock-history-request";
import WarehouseAdminSupplierData, {IWarehouseAdminSupplierData} from "@/http/data/warehouse-admin-supplier-data";

import WarehouseAdminSupplierInvoiceData, {
    IWarehouseAdminSupplierInvoiceData
} from "@/http/data/warehouse-admin-supplier-invoice-data";

import UpdateWarehouseAdminSupplierRequest from "@/http/requests/update-warehouse-admin-supplier-request";
import WarehouseAdminSupplierInvoicesRequest from "@/http/requests/warehouse-admin-supplier-invoices-request";
import SaveWarehouseAdminSupplierRequest from "@/http/requests/save-warehouse-admin-supplier-request";
import WarehouseAdminOrderBuildingReportData, {
    IWarehouseAdminOrderBuildingReportData
} from "@/http/data/warehouse-admin-order-building-report-data";
import WarehouseAdminOrderBuildingReportRequest from "@/http/requests/warehouse-admin-order-building-report-request";

import WarehouseAdminDeliveredTotalsByBuildingReportData, {
    IWarehouseAdminDeliveredTotalsByBuildingReportData
} from "@/http/data/warehouse-admin-delivered-totals-by-building-report-data";

import WarehouseAdminDeliveredTotalsByBuildingReportRequest
    from "@/http/requests/warehouse-admin-delivered-totals-by-building-report-request";

import WarehouseAdminSimpleProductReportData, {
    IWarehouseAdminSimpleProductReportData
} from "@/http/data/warehouse-admin-simple-product-report-data";

import WarehouseAdminSimpleProductReportRequest
    from "@/http/requests/warehouse-admin-simple-product-report-request";

import WarehouseAdminDeliveredTotalsByPeriodReportData, {
    IWarehouseAdminDeliveredTotalsByPeriodReportData
} from "@/http/data/warehouse-admin-delivered-totals-by-period-report-data";

import WarehouseAdminDeliveredTotalsByPeriodReportRequest
    from "@/http/requests/warehouse-admin-delivered-totals-by-period-report-request";

import WarehouseAdminResupplyTaskData, {
    IWarehouseAdminResupplyTaskData
} from "@/http/data/warehouse-admin-resupply-task-data";

import WarehouseAdminResupplyTasksRequest
    from "@/http/requests/warehouse-admin-resupply-tasks-request";

function unwrapObject<T>(payload: unknown): T|null {
    if (!payload || typeof payload !== "object") return null;

    const data = payload as Record<string, unknown>;

    if (data.data && typeof data.data === "object") {
        return data.data as T;
    }

    return data as T;
}

function buildProductFormData(
    request: SaveWarehouseAdminProductRequest,
    image?: File|null
): FormData {
    const formData = new FormData();

    const data = request.toJson() as Record<string, unknown>;

    Object.entries(data).forEach(([key, value]) => {
        if (value === null || value === undefined) return;

        if (typeof value === "boolean") {
            formData.append(key, value ? "1" : "0");
            return;
        }

        formData.append(key, String(value));
    });

    if (image) formData.append("image", image);

    return formData;
}

function unwrapCollection<T>(payload: unknown): T[] {
    if (Array.isArray(payload)) return payload as T[];
    if (!payload || typeof payload !== "object") return [];

    const data = payload as Record<string, unknown>;

    return Array.isArray(data.data)
        ? data.data as T[]
        : [];
}

function responseStatus(response: AxiosResponse): WarehouseAdminStatus|null {
    if (response.status === 401) {
        return WarehouseAdminStatus.Unauthenticated;
    }

    if (response.status === 403) {
        return WarehouseAdminStatus.Forbidden;
    }

    if (response.status < 200 || response.status >= 300) {
        return WarehouseAdminStatus.Failed;
    }

    return null;
}

function orderFromResponse(response: AxiosResponse): WarehouseAdminOrderData|null {
    const data = unwrapObject<IWarehouseAdminOrderData>(
        response.data
    );

    return data
        ? new WarehouseAdminOrderData(data)
        : null;
}

export interface SaveWarehouseAdminProductPayload {
    productId?: number;
    request: SaveWarehouseAdminProductRequest;
    image?: File|null;
}

export interface SaveWarehouseAdminProductSupplierPayload {
    productId: number;
    productSupplierId?: number;
    request: SaveWarehouseAdminProductSupplierRequest;
}

export interface SaveWarehouseAdminProductBuildingSpecificationPayload {
    productId: number;
    building: string;
    request: SaveWarehouseAdminProductBuildingSpecificationRequest;
}

export interface SaveWarehouseAdminProductLocationPayload {
    productId: number;
    productLocationId?: number;
    request: SaveWarehouseAdminProductLocationRequest;
}

export default class WarehouseAdminActions extends Actions<
    WarehouseAdminState,
    WarehouseAdminGetters,
    WarehouseAdminMutations,
    WarehouseAdminActions
> {
    private rootContext!: Context<typeof root>;

    $init(_store: Store<typeof store>): void {
        this.rootContext = root.context(_store);
        super.$init(_store);
    }

    async initialize(): Promise<void> {
        this.mutations.setIsInitialized(true);
    }

    async rehydrateStore(): Promise<void> {}

    async loadProducts(
        request: WarehouseAdminProductsRequest = new WarehouseAdminProductsRequest()
    ): Promise<void> {
        this.mutations.setProductsStatus(WarehouseAdminStatus.Loading);

        const response = await esquireClient
            .get("warehouse-admin/products", {
                params: request.toJson()
            })
            .catch((error: AxiosError) => error.response);

        if (!response) {
            this.mutations.setProductsStatus(WarehouseAdminStatus.Failed);
            return;
        }

        const status = responseStatus(response);

        if (status) {
            this.mutations.setProductsStatus(status);
            return;
        }

        const products = unwrapCollection<IWarehouseAdminProductListData>(
            response.data
        ).map(product => new WarehouseAdminProductListData(product));

        this.mutations.setProducts(products);
        this.mutations.setProductsStatus(
            products.length > 0
                ? WarehouseAdminStatus.Ready
                : WarehouseAdminStatus.Empty
        );
    }

    async loadProduct(productId: number): Promise<void> {
        this.mutations.setProductStatus(WarehouseAdminStatus.Loading);

        const response = await esquireClient
            .get(`warehouse-admin/products/${productId}`)
            .catch((error: AxiosError) => error.response);

        if (!response) {
            this.mutations.setProductStatus(WarehouseAdminStatus.Failed);
            return;
        }

        const status = responseStatus(response);

        if (status) {
            this.mutations.setProductStatus(status);
            return;
        }

        const data = unwrapObject<IWarehouseAdminProductData>(response.data);

        if (!data) {
            this.mutations.setProductStatus(WarehouseAdminStatus.Failed);
            return;
        }

        this.mutations.setProduct(new WarehouseAdminProductData(data));
        this.mutations.setProductStatus(WarehouseAdminStatus.Ready);
    }

    async loadOptions(): Promise<void> {
        this.mutations.setOptionsStatus(WarehouseAdminStatus.Loading);

        const response = await esquireClient
            .get("warehouse-admin/options")
            .catch((error: AxiosError) => error.response);

        if (!response) {
            this.mutations.setOptionsStatus(WarehouseAdminStatus.Failed);
            return;
        }

        const status = responseStatus(response);

        if (status) {
            this.mutations.setOptionsStatus(status);
            return;
        }

        const data = unwrapObject<IWarehouseAdminOptionsData>(response.data);

        if (!data) {
            this.mutations.setOptionsStatus(WarehouseAdminStatus.Failed);
            return;
        }

        this.mutations.setOptions(new WarehouseAdminOptionsData(data));
        this.mutations.setOptionsStatus(WarehouseAdminStatus.Ready);
    }

    async saveProduct(payload: SaveWarehouseAdminProductPayload): Promise<WarehouseAdminProductData|null> {
        this.mutations.setIsSaving(true);

        const formData = buildProductFormData(
            payload.request,
            payload.image
        );

        let url = "warehouse-admin/products";

        if (payload.productId) {
            url += `/${payload.productId}`;

            formData.append("_method", "PUT");
        }

        const response = await esquireClient
            .post(url, formData)
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response) {
            await this.rootContext.actions.message({
                message: "Product could not be saved.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return null;
        }

        const status = responseStatus(response);

        if (status) {
            await this.rootContext.actions.message({
                message: response.data?.message ?? "Product could not be saved.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return null;
        }

        const data = unwrapObject<IWarehouseAdminProductData>(
            response.data
        );

        if (!data) return null;

        const product = new WarehouseAdminProductData(data);

        this.mutations.setProduct(product);
        this.mutations.setProductStatus(WarehouseAdminStatus.Ready);

        await this.rootContext.actions.message({
            message: "Product successfully saved.",
            type: "success",
            duration: 4000,
            showClose: true
        });

        return product;
    }

    async saveProductSupplier(
        payload: SaveWarehouseAdminProductSupplierPayload
    ): Promise<boolean> {
        this.mutations.setIsSaving(true);

        const response = payload.productSupplierId
            ? await esquireClient
                .put(
                    `warehouse-admin/products/${payload.productId}/suppliers/${payload.productSupplierId}`,
                    payload.request.toJson()
                )
                .catch((error: AxiosError) => error.response)
            : await esquireClient
                .post(
                    `warehouse-admin/products/${payload.productId}/suppliers`,
                    payload.request.toJson()
                )
                .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Supplier could not be saved.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return false;
        }

        await this.dispatch("loadProduct", payload.productId);

        await this.rootContext.actions.message({
            message: "Supplier successfully saved.",
            type: "success",
            duration: 4000,
            showClose: true
        });

        return true;
    }

    async deleteProductSupplier(payload: {
        productId: number;
        productSupplierId: number;
    }): Promise<boolean> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .delete(
                `warehouse-admin/products/${payload.productId}/suppliers/${payload.productSupplierId}`
            )
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Supplier could not be removed.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return false;
        }

        await this.dispatch("loadProduct", payload.productId);

        await this.rootContext.actions.message({
            message: "Supplier successfully removed.",
            type: "success",
            duration: 4000,
            showClose: true
        });

        return true;
    }

    async saveProductBuildingSpecification(
        payload: SaveWarehouseAdminProductBuildingSpecificationPayload
    ): Promise<boolean> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .put(
                `warehouse-admin/products/${payload.productId}/building-specifications/${payload.building}`,
                payload.request.toJson()
            )
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Building settings could not be saved.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return false;
        }

        await this.dispatch("loadProduct", payload.productId);

        return true;
    }

    async deleteProductBuildingSpecification(payload: {
        productId: number;
        building: string;
    }): Promise<boolean> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .delete(
                `warehouse-admin/products/${payload.productId}/building-specifications/${payload.building}`
            )
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Building settings could not be removed.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return false;
        }

        await this.dispatch("loadProduct", payload.productId);

        return true;
    }

    async saveProductLocation(
        payload: SaveWarehouseAdminProductLocationPayload
    ): Promise<boolean> {
        this.mutations.setIsSaving(true);

        const response = payload.productLocationId
            ? await esquireClient
                .put(
                    `warehouse-admin/products/${payload.productId}/locations/${payload.productLocationId}`,
                    payload.request.toJson()
                )
                .catch((error: AxiosError) => error.response)
            : await esquireClient
                .post(
                    `warehouse-admin/products/${payload.productId}/locations`,
                    payload.request.toJson()
                )
                .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Location could not be saved.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return false;
        }

        await this.dispatch("loadProduct", payload.productId);

        return true;
    }

    async deleteProductLocation(payload: {
        productId: number;
        productLocationId: number;
    }): Promise<boolean> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .delete(
                `warehouse-admin/products/${payload.productId}/locations/${payload.productLocationId}`
            )
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Location could not be removed.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return false;
        }

        await this.dispatch("loadProduct", payload.productId);

        return true;
    }

    async loadOrders(request: WarehouseAdminOrdersRequest): Promise<void> {
        this.mutations.setOrdersStatus(WarehouseAdminStatus.Loading);

        const response = await esquireClient
            .get(
                "warehouse-admin/orders",
                {
                    params: request.toJson()
                }
            )
            .catch((error: AxiosError) => error.response);

        if (!response) {
            this.mutations.setOrders([]);
            this.mutations.setOrdersStatus(WarehouseAdminStatus.Failed);
            return;
        }

        const status = responseStatus(response);

        if (status) {
            this.mutations.setOrders([]);
            this.mutations.setOrdersStatus(status);
            return;
        }

        const data = unwrapCollection<IWarehouseAdminOrderData>(
            response.data
        );

        const orders = data.map(
            order => new WarehouseAdminOrderData(order)
        );

        this.mutations.setOrders(orders);

        this.mutations.setOrdersStatus(
            orders.length
                ? WarehouseAdminStatus.Ready
                : WarehouseAdminStatus.Empty
        );
    }

    async loadOrder(orderId: number): Promise<void> {
        this.mutations.setOrderStatus(WarehouseAdminStatus.Loading);

        const response = await esquireClient
            .get(`warehouse-admin/orders/${orderId}`)
            .catch((error: AxiosError) => error.response);

        if (!response) {
            this.mutations.setOrder(null);
            this.mutations.setOrderStatus(WarehouseAdminStatus.Failed);
            return;
        }

        const status = responseStatus(response);

        if (status) {
            this.mutations.setOrder(null);
            this.mutations.setOrderStatus(status);
            return;
        }

        const order = orderFromResponse(response);

        if (!order) {
            this.mutations.setOrder(null);
            this.mutations.setOrderStatus(WarehouseAdminStatus.Failed);
            return;
        }

        this.mutations.setOrder(order);
        this.mutations.setOrderStatus(WarehouseAdminStatus.Ready);
    }

    async closeOrder(orderId: number): Promise<WarehouseAdminOrderData|null> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .post(`warehouse-admin/orders/${orderId}/close`)
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Order could not be closed.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return null;
        }

        const order = orderFromResponse(response);

        if (order) {
            this.mutations.setOrder(order);
            this.mutations.setOrderStatus(WarehouseAdminStatus.Ready);
        }

        return order;
    }

    async cancelOrder(orderId: number): Promise<boolean> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .post(`warehouse-admin/orders/${orderId}/cancel`)
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Order could not be cancelled.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return false;
        }

        this.mutations.setOrder(null);

        return true;
    }

    async updateOrderItemLoaded(payload: {
        orderId: number;
        itemId: number;
        request: UpdateWarehouseAdminOrderItemLoadedRequest;
    }): Promise<WarehouseAdminOrderData|null> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .put(
                `warehouse-admin/orders/${payload.orderId}/items/${payload.itemId}/loaded`,
                payload.request.toJson()
            )
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Loaded amount could not be saved.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return null;
        }

        const order = orderFromResponse(response);

        if (order) this.mutations.setOrder(order);

        return order;
    }

    async undoOrderItemLoaded(payload: {
        orderId: number;
        itemId: number;
    }): Promise<WarehouseAdminOrderData|null> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .delete(
                `warehouse-admin/orders/${payload.orderId}/items/${payload.itemId}/loaded`
            )
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Loaded amount could not be reset.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return null;
        }

        const order = orderFromResponse(response);

        if (order) this.mutations.setOrder(order);

        return order;
    }

    async completeOrderLoad(
        orderId: number
    ): Promise<WarehouseAdminOrderData|null> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .post(`warehouse-admin/orders/${orderId}/load`)
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Order could not be marked as loaded.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return null;
        }

        const order = orderFromResponse(response);

        if (order) this.mutations.setOrder(order);

        return order;
    }

    async updateOrderItemDelivered(payload: {
        orderId: number;
        itemId: number;
        request: UpdateWarehouseAdminOrderItemDeliveredRequest;
    }): Promise<WarehouseAdminOrderData|null> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .put(
                `warehouse-admin/orders/${payload.orderId}/items/${payload.itemId}/delivered`,
                payload.request.toJson()
            )
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Delivery amount could not be saved.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return null;
        }

        const order = orderFromResponse(response);

        if (order) this.mutations.setOrder(order);

        return order;
    }

    async undoOrderItemDelivered(payload: {
        orderId: number;
        itemId: number;
    }): Promise<WarehouseAdminOrderData|null> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .delete(
                `warehouse-admin/orders/${payload.orderId}/items/${payload.itemId}/delivered`
            )
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Delivery amount could not be reset.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return null;
        }

        const order = orderFromResponse(response);

        if (order) this.mutations.setOrder(order);

        return order;
    }

    async confirmOrderDelivery(payload: {
        orderId: number;
        role: WarehouseDeliveryConfirmationRole;
        request: ConfirmWarehouseAdminOrderDeliveryRequest;
    }): Promise<WarehouseAdminOrderData|null> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .put(
                `warehouse-admin/orders/${payload.orderId}/delivery-confirmations/${payload.role}`,
                payload.request.toJson()
            )
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Delivery confirmation could not be saved.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return null;
        }

        const order = orderFromResponse(response);

        if (order) this.mutations.setOrder(order);

        return order;
    }

    async undoOrderDeliveryConfirmation(payload: {
        orderId: number;
        role: WarehouseDeliveryConfirmationRole;
    }): Promise<WarehouseAdminOrderData|null> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .delete(
                `warehouse-admin/orders/${payload.orderId}/delivery-confirmations/${payload.role}`
            )
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Delivery confirmation could not be reset.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return null;
        }

        const order = orderFromResponse(response);

        if (order) this.mutations.setOrder(order);

        return order;
    }

    async completeOrderDelivery(orderId: number): Promise<WarehouseAdminOrderData|null> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .patch(`warehouse-admin/orders/${orderId}/deliver`)
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Order could not be marked as delivered.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return null;
        }

        const order = orderFromResponse(response);

        if (order) this.mutations.setOrder(order);

        return order;
    }

    async updateOrderItemReturned(payload: {
        orderId: number;
        itemId: number;
        request: UpdateWarehouseAdminOrderItemReturnedRequest;
    }): Promise<WarehouseAdminOrderData|null> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .put(
                `warehouse-admin/orders/${payload.orderId}/items/${payload.itemId}/returned`,
                payload.request.toJson()
            )
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Returned amount could not be saved.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return null;
        }

        const order = orderFromResponse(response);

        if (order) this.mutations.setOrder(order);

        return order;
    }

    async undoOrderItemReturned(payload: {
        orderId: number;
        itemId: number;
    }): Promise<WarehouseAdminOrderData|null> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .delete(
                `warehouse-admin/orders/${payload.orderId}/items/${payload.itemId}/returned`
            )
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Returned amount could not be reset.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return null;
        }

        const order = orderFromResponse(response);

        if (order) this.mutations.setOrder(order);

        return order;
    }

    async completeOrderReturn(orderId: number): Promise<WarehouseAdminOrderData|null> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .post(`warehouse-admin/orders/${orderId}/return`)
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Order return could not be completed.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return null;
        }

        const order = orderFromResponse(response);

        if (order) this.mutations.setOrder(order);

        return order;
    }

    async loadStock(building: string): Promise<void> {
        this.mutations.setStock([]);
        this.mutations.setStockBuilding(null);
        this.mutations.setStockStatus(WarehouseAdminStatus.Loading);

        const response = await esquireClient
            .get("warehouse-admin/stock", {
                params: {
                    building
                }
            })
            .catch((error: AxiosError) => error.response);

        if (!response) {
            this.mutations.setStockStatus(WarehouseAdminStatus.Failed);
            return;
        }

        const status = responseStatus(response);

        if (status) {
            this.mutations.setStockStatus(status);
            return;
        }

        const data = unwrapCollection<IWarehouseAdminStockProductData>(
            response.data
        );

        const stock = data.map(
            item => new WarehouseAdminStockProductData(item)
        );

        this.mutations.setStock(stock);
        this.mutations.setStockBuilding(building);
        this.mutations.setStockStatus(
            stock.length
                ? WarehouseAdminStatus.Ready
                : WarehouseAdminStatus.Empty
        );
    }

    async saveStock(payload: {
        building: string;
        request: SaveWarehouseAdminStockRequest;
    }): Promise<boolean> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .post(
                "warehouse-admin/stock",
                payload.request.toJson()
            )
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Stock count could not be saved.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return false;
        }

        await this.dispatch("loadStock", payload.building);

        return true;
    }

    async loadRestocks(): Promise<void> {
        this.mutations.setRestocksStatus(
            WarehouseAdminStatus.Loading
        );

        const response = await esquireClient
            .get("warehouse-admin/restocks")
            .catch((error: AxiosError) => error.response);

        if (!response) {
            this.mutations.setRestocks([]);
            this.mutations.setRestocksStatus(
                WarehouseAdminStatus.Failed
            );
            return;
        }

        const status = responseStatus(response);

        if (status) {
            this.mutations.setRestocks([]);
            this.mutations.setRestocksStatus(status);
            return;
        }

        const data = unwrapCollection<IWarehouseAdminRestockData>(
            response.data
        );

        const restocks = data.map(
            item => new WarehouseAdminRestockData(item)
        );

        this.mutations.setRestocks(restocks);

        this.mutations.setRestocksStatus(
            restocks.length
                ? WarehouseAdminStatus.Ready
                : WarehouseAdminStatus.Empty
        );
    }

    async saveRestock(payload: {
        restockId?: number;
        request: SaveWarehouseAdminRestockRequest;
    }): Promise<boolean> {
        this.mutations.setIsSaving(true);

        const response = payload.restockId
            ? await esquireClient
                .put(
                    `warehouse-admin/restocks/${payload.restockId}`,
                    payload.request.toJson()
                )
                .catch((error: AxiosError) => error.response)
            : await esquireClient
                .post(
                    "warehouse-admin/restocks",
                    payload.request.toJson()
                )
                .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message:
                    response?.data?.message
                    ?? "Restock could not be saved.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return false;
        }

        await this.dispatch("loadRestocks");

        return true;
    }

    async deleteRestock(restockId: number): Promise<boolean> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .delete(`warehouse-admin/restocks/${restockId}`)
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message:
                    response?.data?.message
                    ?? "Restock could not be deleted.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return false;
        }

        await this.dispatch("loadRestocks");

        return true;
    }

    async orderRestock(payload: {
        restockId: number;
        request: OrderWarehouseAdminRestockRequest;
    }): Promise<boolean> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .patch(
                `warehouse-admin/restocks/${payload.restockId}/order`,
                payload.request.toJson()
            )
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message:
                    response?.data?.message
                    ?? "Restock could not be ordered.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return false;
        }

        await this.dispatch("loadRestocks");

        return true;
    }

    async checkRestockReceipt(payload: {
        restockId: number;
        request: CheckWarehouseAdminRestockReceiptRequest;
    }): Promise<boolean> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .patch(
                `warehouse-admin/restocks/${payload.restockId}/check-receipt`,
                payload.request.toJson()
            )
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message:
                    response?.data?.message
                    ?? "Receipt check could not be saved.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return false;
        }

        await this.dispatch("loadRestocks");

        return true;
    }

    async receiveRestock(restockId: number): Promise<boolean> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .patch(`warehouse-admin/restocks/${restockId}/receive`)
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message:
                    response?.data?.message
                    ?? "Restock could not be received.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return false;
        }

        await this.dispatch("loadRestocks");

        return true;
    }

    async loadRetours(request: WarehouseAdminRetoursRequest): Promise<void> {
        this.mutations.setRetoursStatus(WarehouseAdminStatus.Loading);

        const response = await esquireClient
            .get("warehouse-admin/retours", {params: request.toJson()})
            .catch((error: AxiosError) => error.response);

        if (!response) {
            this.mutations.setRetours([]);
            this.mutations.setRetoursStatus(WarehouseAdminStatus.Failed);
            return;
        }

        const status = responseStatus(response);

        if (status) {
            this.mutations.setRetours([]);
            this.mutations.setRetoursStatus(status);
            return;
        }

        const data = unwrapCollection<IWarehouseAdminRetourData>(response.data);
        const retours = data.map(retour => new WarehouseAdminRetourData(retour));

        this.mutations.setRetours(retours);
        this.mutations.setRetoursStatus(retours.length ? WarehouseAdminStatus.Ready : WarehouseAdminStatus.Empty);
    }

    async saveRetour(request: SaveWarehouseAdminRetourRequest): Promise<boolean> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .post("warehouse-admin/retours", request.toJson())
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Retour could not be created.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return false;
        }

        return true;
    }

    async loadRetour(payload: {
        retourId: number;
        request: UpdateWarehouseAdminRetourRequest;
    }): Promise<boolean> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .patch(`warehouse-admin/retours/${payload.retourId}/load`, payload.request.toJson())
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Retour could not be loaded.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return false;
        }

        return true;
    }

    async receiveRetour(payload: {
        retourId: number;
        request: UpdateWarehouseAdminRetourRequest;
    }): Promise<boolean> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .patch(`warehouse-admin/retours/${payload.retourId}/receive`, payload.request.toJson())
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Retour could not be received.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return false;
        }

        return true;
    }

    async loadRestockHistory(request: WarehouseAdminRestockHistoryRequest): Promise<void> {
        this.mutations.setRestockHistoryStatus(WarehouseAdminStatus.Loading);

        const response = await esquireClient
            .get("warehouse-admin/restock-history", {
                params: request.toJson()
            })
            .catch((error: AxiosError) => error.response);

        if (!response) {
            this.mutations.setRestockHistory([]);
            this.mutations.setRestockHistoryStatus(WarehouseAdminStatus.Failed);
            return;
        }

        const status = responseStatus(response);

        if (status) {
            this.mutations.setRestockHistory([]);
            this.mutations.setRestockHistoryStatus(status);
            return;
        }

        const data = unwrapCollection<IWarehouseAdminRestockData>(response.data);
        const history = data.map(item => new WarehouseAdminRestockData(item));

        this.mutations.setRestockHistory(history);
        this.mutations.setRestockHistoryStatus(
            history.length
                ? WarehouseAdminStatus.Ready
                : WarehouseAdminStatus.Empty
        );
    }

    async loadSuppliers(): Promise<void> {
        this.mutations.setSuppliersStatus(WarehouseAdminStatus.Loading);

        const response = await esquireClient
            .get("warehouse-admin/suppliers")
            .catch((error: AxiosError) => error.response);

        if (!response) {
            this.mutations.setSuppliers([]);
            this.mutations.setSuppliersStatus(WarehouseAdminStatus.Failed);
            return;
        }

        const status = responseStatus(response);

        if (status) {
            this.mutations.setSuppliers([]);
            this.mutations.setSuppliersStatus(status);
            return;
        }

        const data = unwrapCollection<IWarehouseAdminSupplierData>(response.data);
        const suppliers = data.map(supplier => new WarehouseAdminSupplierData(supplier));

        this.mutations.setSuppliers(suppliers);
        this.mutations.setSuppliersStatus(
            suppliers.length ? WarehouseAdminStatus.Ready : WarehouseAdminStatus.Empty
        );
    }

    async saveSupplierLink(payload: {
        supplierId: number;
        request: UpdateWarehouseAdminSupplierRequest;
    }): Promise<boolean> {
        this.mutations.setIsSaving(true);

        const response = await esquireClient
            .patch(
                `warehouse-admin/suppliers/${payload.supplierId}`,
                payload.request.toJson()
            )
            .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Supplier link could not be saved.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return false;
        }

        this.mutations.setSupplierInvoices([]);
        this.mutations.setSupplierInvoicesStatus(WarehouseAdminStatus.Empty);
        this.mutations.setSupplierInvoicesSupplierId(null);

        await this.dispatch("loadSuppliers");

        await this.rootContext.actions.message({
            message: "Supplier link successfully saved.",
            type: "success",
            duration: 4000,
            showClose: true
        });

        return true;
    }

    async loadSupplierInvoices(payload: {
        supplierId: number;
        request: WarehouseAdminSupplierInvoicesRequest;
    }): Promise<void> {
        this.mutations.setSupplierInvoicesSupplierId(payload.supplierId);
        this.mutations.setSupplierInvoicesStatus(WarehouseAdminStatus.Loading);

        const response = await esquireClient
            .get(
                `warehouse-admin/suppliers/${payload.supplierId}/invoices`,
                {params: payload.request.toJson()}
            )
            .catch((error: AxiosError) => error.response);

        if (!response) {
            this.mutations.setSupplierInvoices([]);
            this.mutations.setSupplierInvoicesStatus(WarehouseAdminStatus.Failed);
            return;
        }

        const status = responseStatus(response);

        if (status) {
            this.mutations.setSupplierInvoices([]);
            this.mutations.setSupplierInvoicesStatus(status);
            return;
        }

        const data = unwrapCollection<IWarehouseAdminSupplierInvoiceData>(response.data);
        const invoices = data.map(invoice => new WarehouseAdminSupplierInvoiceData(invoice));

        this.mutations.setSupplierInvoices(invoices);
        this.mutations.setSupplierInvoicesStatus(
            invoices.length ? WarehouseAdminStatus.Ready : WarehouseAdminStatus.Empty
        );
    }

    async saveSupplier(payload: {
        supplierId?: number;
        request: SaveWarehouseAdminSupplierRequest;
    }): Promise<boolean> {
        this.mutations.setIsSaving(true);

        const response = payload.supplierId
            ? await esquireClient
                .patch(
                    `warehouse-admin/suppliers/${payload.supplierId}`,
                    payload.request.toJson()
                )
                .catch((error: AxiosError) => error.response)
            : await esquireClient
                .post(
                    "warehouse-admin/suppliers",
                    payload.request.toJson()
                )
                .catch((error: AxiosError) => error.response);

        this.mutations.setIsSaving(false);

        if (!response || responseStatus(response)) {
            await this.rootContext.actions.message({
                message: response?.data?.message ?? "Supplier could not be saved.",
                type: "error",
                duration: 0,
                showClose: true
            });

            return false;
        }

        this.mutations.setSupplierInvoices([]);
        this.mutations.setSupplierInvoicesStatus(WarehouseAdminStatus.Empty);
        this.mutations.setSupplierInvoicesSupplierId(null);

        await this.dispatch("loadSuppliers");

        await this.rootContext.actions.message({
            message: "Supplier successfully saved.",
            type: "success",
            duration: 4000,
            showClose: true
        });

        return true;
    }

    async loadOrderBuildingReport(request: WarehouseAdminOrderBuildingReportRequest): Promise<void> {
        this.mutations.setOrderBuildingReportStatus(WarehouseAdminStatus.Loading);

        const response = await esquireClient
            .get("warehouse-admin/reports/order-building-detail", {
                params: request.toJson()
            })
            .catch((error: AxiosError) => error.response);

        if (!response) {
            this.mutations.setOrderBuildingReport(null);
            this.mutations.setOrderBuildingReportStatus(WarehouseAdminStatus.Failed);
            return;
        }

        const status = responseStatus(response);

        if (status) {
            this.mutations.setOrderBuildingReport(null);
            this.mutations.setOrderBuildingReportStatus(status);
            return;
        }

        const data = unwrapObject<IWarehouseAdminOrderBuildingReportData>(response.data);

        if (!data) {
            this.mutations.setOrderBuildingReport(null);
            this.mutations.setOrderBuildingReportStatus(WarehouseAdminStatus.Failed);
            return;
        }

        this.mutations.setOrderBuildingReport(
            new WarehouseAdminOrderBuildingReportData(data)
        );
        this.mutations.setOrderBuildingReportStatus(WarehouseAdminStatus.Ready);
    }

    async loadDeliveredTotalsByBuildingReport(
        request: WarehouseAdminDeliveredTotalsByBuildingReportRequest
    ): Promise<void> {
        this.mutations.setDeliveredTotalsByBuildingReportStatus(WarehouseAdminStatus.Loading);

        const response = await esquireClient
            .get("warehouse-admin/reports/delivered-totals-by-building", {
                params: request.toJson()
            })
            .catch((error: AxiosError) => error.response);

        if (!response) {
            this.mutations.setDeliveredTotalsByBuildingReport(null);
            this.mutations.setDeliveredTotalsByBuildingReportStatus(WarehouseAdminStatus.Failed);
            return;
        }

        const status = responseStatus(response);

        if (status) {
            this.mutations.setDeliveredTotalsByBuildingReport(null);
            this.mutations.setDeliveredTotalsByBuildingReportStatus(status);
            return;
        }

        const data = unwrapObject<IWarehouseAdminDeliveredTotalsByBuildingReportData>(
            response.data
        );

        if (!data) {
            this.mutations.setDeliveredTotalsByBuildingReport(null);
            this.mutations.setDeliveredTotalsByBuildingReportStatus(WarehouseAdminStatus.Failed);
            return;
        }

        this.mutations.setDeliveredTotalsByBuildingReport(
            new WarehouseAdminDeliveredTotalsByBuildingReportData(data)
        );

        this.mutations.setDeliveredTotalsByBuildingReportStatus(
            WarehouseAdminStatus.Ready
        );
    }

    async loadSimpleProductReport(
        request: WarehouseAdminSimpleProductReportRequest = new WarehouseAdminSimpleProductReportRequest()
    ): Promise<void> {
        this.mutations.setSimpleProductReportStatus(WarehouseAdminStatus.Loading);

        const response = await esquireClient
            .get("warehouse-admin/reports/simple-products", {
                params: request.toJson()
            })
            .catch((error: AxiosError) => error.response);

        if (!response) {
            this.mutations.setSimpleProductReport(null);
            this.mutations.setSimpleProductReportStatus(WarehouseAdminStatus.Failed);
            return;
        }

        const status = responseStatus(response);

        if (status) {
            this.mutations.setSimpleProductReport(null);
            this.mutations.setSimpleProductReportStatus(status);
            return;
        }

        const data = unwrapObject<IWarehouseAdminSimpleProductReportData>(
            response.data
        );

        if (!data) {
            this.mutations.setSimpleProductReport(null);
            this.mutations.setSimpleProductReportStatus(WarehouseAdminStatus.Failed);
            return;
        }

        this.mutations.setSimpleProductReport(
            new WarehouseAdminSimpleProductReportData(data)
        );

        this.mutations.setSimpleProductReportStatus(
            WarehouseAdminStatus.Ready
        );
    }

    async loadDeliveredTotalsByPeriodReport(
        request: WarehouseAdminDeliveredTotalsByPeriodReportRequest
    ): Promise<void> {
        this.mutations.setDeliveredTotalsByPeriodReportStatus(WarehouseAdminStatus.Loading);

        const response = await esquireClient
            .get("warehouse-admin/reports/delivered-totals-by-period", {
                params: request.toJson()
            })
            .catch((error: AxiosError) => error.response);

        if (!response) {
            this.mutations.setDeliveredTotalsByPeriodReport(null);
            this.mutations.setDeliveredTotalsByPeriodReportStatus(WarehouseAdminStatus.Failed);
            return;
        }

        const status = responseStatus(response);

        if (status) {
            this.mutations.setDeliveredTotalsByPeriodReport(null);
            this.mutations.setDeliveredTotalsByPeriodReportStatus(status);
            return;
        }

        const data = unwrapObject<IWarehouseAdminDeliveredTotalsByPeriodReportData>(
            response.data
        );

        if (!data) {
            this.mutations.setDeliveredTotalsByPeriodReport(null);
            this.mutations.setDeliveredTotalsByPeriodReportStatus(WarehouseAdminStatus.Failed);
            return;
        }

        this.mutations.setDeliveredTotalsByPeriodReport(
            new WarehouseAdminDeliveredTotalsByPeriodReportData(data)
        );

        this.mutations.setDeliveredTotalsByPeriodReportStatus(
            WarehouseAdminStatus.Ready
        );
    }

    async loadResupplyTasks(
        request: WarehouseAdminResupplyTasksRequest = new WarehouseAdminResupplyTasksRequest()
    ): Promise<void> {
        this.mutations.setResupplyTasks([]);
        this.mutations.setResupplyTasksStatus(WarehouseAdminStatus.Loading);

        const response = await esquireClient
            .get("warehouse-admin/resupply-tasks", {
                params: request.toJson()
            })
            .catch((error: AxiosError) => error.response);

        if (!response) {
            this.mutations.setResupplyTasksStatus(WarehouseAdminStatus.Failed);
            return;
        }

        const status = responseStatus(response);

        if (status) {
            this.mutations.setResupplyTasksStatus(status);
            return;
        }

        const data = unwrapCollection<IWarehouseAdminResupplyTaskData>(
            response.data
        );

        const tasks = data.map(
            task => new WarehouseAdminResupplyTaskData(task)
        );

        this.mutations.setResupplyTasks(tasks);
        this.mutations.setResupplyTasksStatus(
            tasks.length
                ? WarehouseAdminStatus.Ready
                : WarehouseAdminStatus.Empty
        );
    }

    clear(): void {
        this.mutations.clear();
    }
}